<template>
  <div class="chat" :class="classes">
    <div class="chat-container">
      <div class="chat__header">
        <div class="chat__status" :class="`chat__status--${status}`"></div>
        <div class="chat__title">
          {{ chat ? chat.title || "Chat" : "Chat" }}
          <a @click="rulesDialog = true">Chat rules</a>
        </div>
      </div>
      <div class="chat__messages" ref="messages">
        <div v-if="chat">
          <infinite-loading direction="top" @infinite="fetchMessages">
            <div slot="no-more"></div>
            <div slot="no-results"></div>
          </infinite-loading>
          <ChatMessage
            :id="message.id"
            :chatId="chat.id"
            :body="message.body"
            :name="message.user_name"
            :userId="message.user_id"
            :createdAt="message.created_at"
            v-for="message in messages"
            :key="message.uuid"
            @replyTo="replyTo"
          />
        </div>
      </div>
      <div class="chat__footer">
        <ChatFooter :locked="locked" @clear="clear" v-model="input" />
      </div>
    </div>
    <v-dialog max-width="400" v-model="rulesDialog">
      <v-card>
        <v-card-text class="pa-6">
          <v-btn class="close" @click="rulesDialog = false" icon>
            <v-icon>$close</v-icon>
          </v-btn>
          <h2>Chat rules</h2>
          <br />
          <p>
            Welcome to the chat. This chat room will be open during video
            release periods to share reactions with others.
          </p>
          <p>
            This chat is a safe space for all attendees. Please see the rules of
            engagement below:
          </p>
          <ul>
            <li>
              Do not post personal contact information in the chat room (email,
              address, social, etc).
            </li>
            <li>Do not post links in the chat room.</li>
            <li>
              Engaging in disturbing or inappropriate behavior will result in
              immediate removal from the chat room. (No warning required.)
            </li>
            <li>
              Please respect the chat moderators. (Identified by * next to their
              names).
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ChatMessage from "./Chat/ChatMessage";
import ChatFooter from "./Chat/ChatFooter";

export default {
  name: "Chat",
  components: { ChatMessage, ChatFooter },
  props: {
    reciever: Boolean
  },
  data() {
    return {
      messages: [],
      input: "",
      replyToName: "",
      status: "Connecting",
      inited: false,
      chat: null,
      rulesDialog: false
    };
  },
  channels: {
    ChatChannel: {
      connected() {
        console.log("Connected");
        this.status = "Connected";
        window.syncMessages = this.syncMessages;
        if (this.inited) {
          this.syncMessages();
        }
        this.inited = true;
      },
      rejected() {
        console.log("Rejected");
      },
      received({ type, payload }) {
        switch (type) {
          case "update_chat_params":
            this.updateChatParams(payload);
            break;
          case "new_message":
            this.newMessage(payload);
            break;
          case "delete_message":
            this.deleteMessage(payload.id);
            break;
          case "update_message":
            this.updateMessage(payload);
            break;
        }
      },
      disconnected() {
        console.log("Disconnected");
        this.status = "Disconnected";
      }
    }
  },
  mounted() {
    this.setChat();
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 1000);

    window.$cable = this.$cable;
  },
  methods: {
    newMessage(payload) {
      this.messages.push(payload);
      this.scrollBottom();
    },
    deleteMessage(messageId) {
      this.messages = this.messages.filter(m => m.id != messageId);
      this.scrollBottom();
    },
    updateMessage(payload) {
      this.messages = this.messages.map(m => {
        if (m.id == payload.id) {
          m.body = payload.body;
        }
        return m;
      });
      this.scrollBottom();
    },
    updateChatParams(payload) {
      this.chat = payload;
      if (this.reciever) {
        this.$emit("input", payload);
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
        }, 1000);
      }
    },
    async setChat() {
      const { data } = await this.$api.Chat.get({
        page_id: this.activePage?.id
      });
      if (!data) return;
      this.chat = data;
      this.$emit("input", this.chat);

      this.$cable.subscribe({
        channel: "ChatChannel",
        chat_id: this.chat.id
      });
    },
    async syncMessages() {
      const { data } = await this.$api.Message.query({
        chat_id: this.chat.id,
        first_message_id: this.messages[this.messages.length - 1]?.id
      });

      this.messages.push(...data.reverse());
      this.scrollBottom();
    },
    async fetchMessages($state) {
      const { data } = await this.$api.Message.query({
        chat_id: this.chat.id,
        last_message_id: this.messages[0]?.id
      });
      this.messages.unshift(...data.reverse());

      if ($state) {
        if (data.length) {
          $state.loaded();
        } else {
          $state.complete();
        }
      }
    },

    clear() {
      this.replyToName = "";
      this.input = "";
    },

    replyTo(name) {
      this.input =
        name + ", " + this.input.replace(this.replyToName + ", ", "");
      this.replyToName = name;
    },
    scrollBottom() {
      if (!this.$refs.messages) return;
      if (
        this.$refs.messages.scrollTop ==
        this.$refs.messages.scrollHeight - this.$refs.messages.clientHeight
      )
        this.$nextTick(function() {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
        });
    }
  },
  computed: {
    locked() {
      return this.chat?.locked;
    },
    classes() {
      return [
        {
          "chat--editable": this.currentUser?.admin
        }
      ];
    }
  },
  beforeDestroy() {
    this.$cable.unsubscribe({
      channel: "ChatChannel",
      chat_id: this.chat?.id
    });
  }
};
</script>

<style scoped lang="scss">
.chat {
  width: 100%;
  background: white;
  position: relative;
  overflow: hidden;
  border: 1px solid #e7e7e7;
}

.chat--editable {
  &::v-deep .message-actions {
    display: block;
  }
}

.chat__header {
  padding: 0 15px;
  min-height: 48px;
  color: white;
  background-color: $stjude-blue;
}
.chat__messages {
  padding: 15px;
}
.chat__header {
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.chat__status {
  width: 5px;
  min-width: 5px;
  height: 5px;
  display: inline-block;
  border-radius: 50%;
  margin-right: 5px;

  &--Connecting {
    background: $primary;
  }

  &--Connected {
    background: $success;
  }

  &--Disconnected {
    background: $error;
  }
}

.chat__messages {
  background: #f9f9f9;
  position: absolute;
  bottom: 80px;
  top: 50px;
  overflow-y: auto;
  left: 0;
  right: 0;
}

.chat__footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 15px;
  min-height: 80px;
  background: white;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.15);
  z-index: 1;
}

.chat__title {
  font-weight: bold;
  font-size: 12px;
  line-height: 1.2;
  color: white;
  text-transform: uppercase;

  a {
    text-decoration: underline;
    color: white;
    text-transform: initial;
  }
}
</style>
